import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import ProjectHeader from './ProjectHeader';
import { TufteContent } from '../Sidenote';
import { CitationProvider, Bibliography } from '../Citations';
import './ProjectLayout.css';

/*
 * Shared layout for every project page. Wrap a project's body in it:
 *
 *   const MyProject = ({ project }) => (
 *     <ProjectLayout project={project}>
 *       <p>Body text<Sidenote>A note.</Sidenote> goes here.</p>
 *       <h2>A Section</h2>
 *       ...
 *     </ProjectLayout>
 *   );
 *
 * Every project that uses this automatically gets:
 *   - the standard header (back button, title, date),
 *   - Tufte sidenotes / footnotes (just drop in <Sidenote>/<Marginnote>),
 *   - a left-hand table of contents auto-built from the headings (h2–h4) in the
 *     body, with active-section highlighting and smooth-scroll on click,
 *   - LaTeX-style citations: pass a `references` map and cite inline with
 *     <Cite id="key"> / <Citet id="key"> — a numbered References section is
 *     appended automatically (see Citations.js for the entry format).
 */

const slugify = (text, i) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-') || `section-${i}`;

const ProjectLayout = ({ project, children, className = '', references }) => {
  const contentRef = useRef(null);
  // The references prop is an entry map or a raw BibTeX string (see
  // Citations.js); either way only render a bibliography when it's non-empty.
  const hasReferences =
    typeof references === 'string'
      ? references.trim().length > 0
      : references && Object.keys(references).length > 0;
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState(null);

  // Build the table of contents from the headings in the rendered body. Assign
  // a stable id to any heading that doesn't already have one so the TOC links
  // (and deep links) have somewhere to jump to.
  useLayoutEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const headings = Array.from(root.querySelectorAll('h2, h3, h4'));
    const seen = new Set();
    const items = headings.map((node, i) => {
      let id = node.id;
      if (!id) {
        id = slugify(node.textContent, i);
        // De-duplicate identical headings.
        let unique = id;
        let n = 2;
        while (seen.has(unique)) unique = `${id}-${n++}`;
        id = unique;
        node.id = id;
      }
      seen.add(id);
      node.classList.add('project-heading');
      return { id, text: node.textContent, level: Number(node.tagName.charAt(1)) };
    });
    setToc(items);
  }, [children]);

  // Highlight whichever section is currently near the top of the viewport.
  useEffect(() => {
    if (toc.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '0px 0px -75% 0px', threshold: 0 }
    );
    toc.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [toc]);

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  };

  return (
    <div className={`project-layout ${className}`.trim()}>
      <div className="project-layout-header">
        <ProjectHeader
          title={project.title}
          date={project.date}
          featured={project.featured}
        />
      </div>

      <div className="project-layout-body">
        {toc.length > 0 && (
          <nav className="project-toc" aria-label="Table of contents">
            <div className="project-toc-title">Contents</div>
            <ul className="project-toc-list">
              {toc.map((item) => (
                <li
                  key={item.id}
                  className={`toc-item toc-level-${item.level}${
                    activeId === item.id ? ' toc-active' : ''
                  }`}
                >
                  <a href={`#${item.id}`} onClick={(e) => handleTocClick(e, item.id)}>
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="project-main">
          <CitationProvider references={references || {}}>
            <TufteContent className="project-content">
              <div ref={contentRef} className="project-body">
                {children}
                {hasReferences && <Bibliography />}
              </div>
            </TufteContent>
          </CitationProvider>
        </div>
      </div>
    </div>
  );
};

export default ProjectLayout;
